/**
 * Manages DOM element caching and retrieval for better performance.
 * Prevents repeated DOM queries by caching elements on first access.
 */
class DOMCache {
    constructor() {
        this.cache = new Map();
    }

    /**
     * Get element by ID, cached for future use.
     * @param {string} id - Element ID
     * @returns {HTMLElement|null}
     */
    getElementById(id) {
        if (this.cache.has(id)) {
            return this.cache.get(id);
        }

        const element = document.getElementById(id);
        if (element) {
            this.cache.set(id, element);
        }
        return element;
    }

    /**
     * Get first element matching selector, cached for future use.
     * @param {string} selector - CSS selector
     * @param {string} [cacheKey] - Optional custom cache key
     * @returns {HTMLElement|null}
     */
    querySelector(selector, cacheKey = null) {
        const key = cacheKey || selector;
        
        if (this.cache.has(key)) {
            return this.cache.get(key);
        }

        const element = document.querySelector(selector);
        if (element) {
            this.cache.set(key, element);
        }
        return element;
    }

    /**
     * Get all elements matching selector, cached for future use.
     * @param {string} selector - CSS selector
     * @param {string} [cacheKey] - Optional custom cache key
     * @returns {NodeList}
     */
    querySelectorAll(selector, cacheKey = null) {
        const key = cacheKey || `all_${selector}`;
        
        if (this.cache.has(key)) {
            return this.cache.get(key);
        }

        const elements = document.querySelectorAll(selector);
        this.cache.set(key, elements);
        return elements;
    }

    /**
     * Find element within parent using selector, cached for future use.
     * @param {HTMLElement} parent - Parent element
     * @param {string} selector - CSS selector
     * @param {string} cacheKey - Custom cache key
     * @returns {HTMLElement|null}
     */
    findInParent(parent, selector, cacheKey) {
        if (!parent) {
            return null;
        }

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const element = parent.querySelector(selector);
        if (element) {
            this.cache.set(cacheKey, element);
        }
        return element;
    }

    /**
     * Find all elements within parent using selector, cached for future use.
     * @param {HTMLElement} parent - Parent element
     * @param {string} selector - CSS selector
     * @param {string} cacheKey - Custom cache key
     * @returns {NodeList}
     */
    findAllInParent(parent, selector, cacheKey) {
        if (!parent) {
            return [];
        }

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const elements = parent.querySelectorAll(selector);
        this.cache.set(cacheKey, elements);
        return elements;
    }

    /**
     * Get element attribute, cached for future use.
     * @param {HTMLElement} element - Element
     * @param {string} attribute - Attribute name
     * @param {string} cacheKey - Custom cache key
     * @returns {string|null}
     */
    getAttribute(element, attribute, cacheKey) {
        if (!element) {
            return null;
        }

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const value = element.getAttribute(attribute);
        this.cache.set(cacheKey, value);
        return value;
    }

    /**
     * Check if element exists in cache.
     * @param {string} key - Cache key
     * @returns {boolean}
     */
    has(key) {
        return this.cache.has(key);
    }

    /**
     * Get cached value by key.
     * @param {string} key - Cache key
     * @returns {*} - Cached value
     */
    get(key) {
        return this.cache.get(key);
    }

    /**
     * Set value in cache.
     * @param {string} key - Cache key
     * @param {*} value - Value to cache
     */
    set(key, value) {
        this.cache.set(key, value);
    }

    /**
     * Clear all cached elements.
     */
    clear() {
        this.cache.clear();
    }

    /**
     * Remove specific entry from cache.
     * @param {string} key - Cache key to remove
     */
    remove(key) {
        this.cache.delete(key);
    }

    /**
     * Get cache statistics.
     * @returns {Object} - Cache stats
     */
    getStats() {
        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys())
        };
    }
}

export default DOMCache;
